import {
  IDisplayPrice,
  IOCAPIProductPromotion,
  IProduct,
  IProductImage,
  IContentProductIds
} from '../types';
import uniq from "lodash/uniq";

export const poll = (fn: () => boolean, timeout: number, interval: number) => {
  var endTime = Number(new Date()) + (timeout || 2000);
  interval = interval || 100;

  var checkCondition = function (resolve: any, reject: any) {
    // If the condition is met, we're done!
    var result = fn();
    if (result) {
      resolve(result);
    }
    // If the condition isn't met but the timeout hasn't elapsed, go again
    else if (Number(new Date()) < endTime) {
      setTimeout(checkCondition, interval, resolve, reject);
    }
    // Didn't match and too much time, reject!
    else {
      reject(new Error('timed out for ' + fn + ': ' + arguments));
    }
  };

  return new Promise<boolean>(checkCondition);
};

export const pollWindowVariable = async (
  property: keyof Window,
  timeout: number = 2000
) => {
  try {
    const w = window as any;
    await poll(
      () => {
        return w[property];
      },
      timeout,
      150
    );

    return w[property];
  } catch (error) {
    return null;
  }
};

export const pollWindowVariables = (properties: Array<keyof Window>) => {
  const promises = properties.map((property) => {
    return new Promise((resolve) => resolve(pollWindowVariable(property)));
  });

  return promises;
};

export const formatMasterProductPrice = (
  price: number,
  productDisplayPrice: string
) => {
  let displayPrice = JSON.parse(productDisplayPrice),
    promoPrice = price,
    regularPrice = price;

  if (price === null) {
    return null;
  }

  if (typeof displayPrice.promo_price !== 'object') {
    promoPrice = displayPrice.promo_price;
  }

  /**
   * when the regular price is returned as a object,
   * and only the promo price is setup use, promo price as regular
   */
  if (typeof displayPrice.regular === 'object') {
    regularPrice = promoPrice;
  } else {
    regularPrice = displayPrice.regular;
  }

  return {
    ...displayPrice,
    regular: regularPrice,
    promo_price: promoPrice
  };
};

export const formatVariantProductPrice = (
  product: any
): IDisplayPrice | null => {
  const promos: IOCAPIProductPromotion[] = product.product_promotions.filter(
    (promotion: any) => promotion.hasOwnProperty('promotional_price')
  );

  if (!promos.length) {
    return {
      regular: product.price,
      promo_price: null,
      has_same_variant_prices: true,
      percent_off: 0
    };
  }

  const promoPrice = promos.reduce((min, cur) => {
    return cur.promotional_price < min ? cur.promotional_price : min;
  }, promos[0].promotional_price);

  /**
   * Custom product data from OCAPI response.
   * You can add other properties from the OCAPI response when needed
   */
  const formatPromoPrice = {
    regular: product.price,
    promo_price: promoPrice,
    has_same_variant_prices: true,
    percent_off: getPercentOff(product.price, promoPrice)
  };

  return formatPromoPrice;
};

const getPercentOff = (regular: number, promo: number): number => {
  const difference = regular - promo;
  return Math.round(100 - (difference * 100) / regular) / 100;
};

export const getProductIds = (
  data: any,
  ids: IContentProductIds = { variantIds: [], masterIds: [] }
): IContentProductIds | null => {
  if (Array.isArray(data)) {
    data.forEach((dataValue) => getProductIds(dataValue, ids));
  } else {
    if (data instanceof Object && data.hasOwnProperty("product_id") && data.hasOwnProperty("product_type")) {
      if (data.product_type === "variant" && data.product_id) {
        ids.variantIds.push(cleanSpaces(data.product_id));
      } else if (data.product_type === "master" && data.product_id) {
        ids.masterIds.push(cleanSpaces(data.product_id));
      }
    } else if (data instanceof Object) {
      const values = Object.keys(data).map((e) => data[e]);
      values.forEach((dataValue: any) => {
        if (dataValue instanceof Object) {
          getProductIds(dataValue, ids);
        }
      });
    }
  }

  ids.masterIds = uniq(ids.masterIds);
  ids.variantIds = uniq(ids.variantIds);

  const containsIds = ids.masterIds.length !== 0 || ids.variantIds.length !== 0;
  return containsIds ? ids : null;
};

export function sendEvent(action: string, additionalParams: any = {}) {
  if (typeof window.tc_events_100 !== 'undefined') {
    // @ts-ignore
    window.tc_events_100(this, 'clickevent', {
      ACTION: action,
      ...additionalParams
    });
  }
}

export const cleanSpaces = (id: string) => id ? id.replace('\t','').trim() : null;

export const sendWaData = async ({
  siteSection,
  pageName
}: {
  siteSection: string;
  pageName: string;
}) => {
  try {
    const wa_data = await pollWindowVariable('wa_data');
    if (wa_data) {
      setTimeout(() => {
        wa_data.siteSection = siteSection;
        wa_data.pageName = pageName;
      }, 0);
    }
  } catch (error) {
    console.error(error);
  }
};

export const getShopURL = () => {
  const isUPC = process.env.REACT_APP_ENV === "upc";
  return isUPC
    ? '/upc'
    : '';
};

export const getProductURL = (productID: string, countryCode: string) => {
  return `${getShopURL()}/${getCountryCodeForUrls(countryCode)}/${productID}.html`;
};

export const getCountryCodeForUrls = (countryCode: string) => {
  return countryCode === 'pt' || countryCode === 'la' || countryCode === 'ia'
    ? 'ofertas'
    : countryCode;
};

export const getRedirection = (
  type: string,
  value: string,
  countryCode?: string
) => {
  switch (type) {
    case 'category':
      return `$url('Search-Show','cgid','${value}')$`;

    case 'product':
      return `$url('Product-Show', 'pid', '${value}')$`;

    case 'custom':
      return `${getShopURL()}/${getCountryCodeForUrls(countryCode)}/${value}`;

    default:
      return null;
  }
};

/**
 * Get images from GoogleSheet JSON
 */
export const getImages = (
  data: any,
  basePath: string,
  dictionnary: { [key: string]: string } = {}
) => {
  const imageRegex = /(.*)\.(jpg|gif|png|jpeg)/gi;
  if (Array.isArray(data)) {
    data.forEach((dataValue) => getImages(dataValue, basePath, dictionnary));
  } else if (typeof data === 'string') {
    let matches = null;
    while ((matches = imageRegex.exec(data)) !== null) {
      const match = matches[0];
      const filename = matches[1];

      dictionnary[filename] = `${basePath}/${match}`;
    }
  } else if (data instanceof Object) {
    const values = Object.keys(data).map((e) => data[e]);
    values.forEach((dataValue: any) => {
      if (dataValue) {
        getImages(dataValue, basePath, dictionnary);
      }
    });
  }

  return dictionnary;
};

export const getImageFileName = (image: string) => {
  const extensionRegex = /(.*)\.(jpg|gif|png|jpeg)/gi;
  const matches = extensionRegex.exec(image);
  const filename = matches[1];

  return filename;
};

/**
 * @function getProductImage
 * @param  {type} productImages: any {The image_groups property of product OCAPI response}
 * @return {type} {Should return an object with the product box shot}
 */
export const getProductImage = (productImages: any): IProductImage | null => {
  const images = productImages.filter(
    (group: any) => group.view_type === 'large'
  )[0].images;
  const regex = /-\d*.(jpg|jpeg|png)/;
  const boxShotImage = images
    .filter((image: any) => {
      const isBoxShotImage = !regex.test(image.link);
      if (isBoxShotImage) {
        return image;
      }
    })
    .reduce((acc: any, cur: any) => Object.assign(acc, cur), {});

  return boxShotImage.link
    ? {
        name: boxShotImage.title,
        link: boxShotImage.link
      }
    : null;
};

export const camelize = (str: string) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');
};

export const splitArrayIntoChunk = (array: string[], max: number) => {
  const initialValue: string[][] = [];

  const chunkedValue = array.reduce((acc, cur) => {
    if (acc.length === 0) {
      acc.push([cur]);
      return acc;
    }

    const currentString = [...acc[acc.length - 1], cur].join(',');

    if (currentString.length < max) {
      acc[acc.length - 1] = [...acc[acc.length - 1], cur];
      return acc;
    } else {
      acc.push([cur]);
    }

    return acc;
  }, initialValue);

  return chunkedValue;
};

export const getProductById = (
  products: IProduct[],
  id: string,
  type?: 'variant' | 'master'
): IProduct | null => {
  const defaultProductKey = !type || type === 'master' ? 'MasterID' : 'id';
  const productFound = products.filter(function (product) {
    if (product['MasterID'] !== id && product['id'] !== id) {
      return null;
    }

    return (
      product[defaultProductKey] === id ||
      product['id'] === id ||
      product['MasterID'] === id
    );
  });

  return productFound[0];
};
